import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Check } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (s.data.payment_status === "paid") {
          setStatus("paid");
          const o = await api.get(`/orders/by-session/${sessionId}`);
          setOrder(o.data);
          clear();
          return;
        }
        if (["expired", "failed"].includes(s.data.payment_status)) {
          setStatus("failed");
          return;
        }
        if (attempts >= 10) { setStatus("pending"); return; }
        setAttempts((a) => a + 1);
        setTimeout(tick, 2000);
      } catch { setStatus("failed"); }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="pt-32 min-h-screen max-w-3xl mx-auto px-6 lg:px-12 text-center">
      {status === "checking" && <div><div className="overline">Confirming Payment…</div><div className="font-serif-editorial text-4xl mt-4">One moment.</div></div>}
      {status === "paid" && (
        <div>
          <div className="w-16 h-16 border border-[#1C1C1C] rounded-full flex items-center justify-center mx-auto"><Check size={24} strokeWidth={1.25} /></div>
          <div className="overline mt-8">Payment Received</div>
          <h1 className="font-serif-editorial text-5xl lg:text-6xl mt-4 leading-tight">Your suite is being<br /><em className="italic">prepared.</em></h1>
          <p className="mt-6 text-[#5C5C5C]">A concierge will reach out within 24 hours to coordinate delivery, installation, and training.</p>
          {order && (
            <div className="mt-12 border border-[#E5E5E5] p-8 text-left">
              <div className="overline mb-6">Order Summary</div>
              {order.items.map((i) => (
                <div key={i.slug} className="flex justify-between py-4 border-b border-[#E5E5E5] last:border-0">
                  <div>
                    <div className="font-serif-editorial text-xl">{i.name}</div>
                    <div className="text-xs text-[#5C5C5C] mt-1">Qty {i.quantity}</div>
                  </div>
                  <div className="font-serif-editorial text-xl">{fmt(i.subtotal)}</div>
                </div>
              ))}
              <div className="flex justify-between mt-6 pt-6 border-t border-[#1C1C1C]">
                <div className="overline">Total</div>
                <div className="font-serif-editorial text-3xl">{fmt(order.amount)}</div>
              </div>
            </div>
          )}
          <Link to="/shop" data-testid="continue-shopping-btn" className="tt-btn-outline mt-12">Continue Browsing</Link>
        </div>
      )}
      {status === "pending" && <div><div className="overline">Still Processing</div><p className="mt-4 text-[#5C5C5C]">Your order confirmation will arrive shortly by email.</p></div>}
      {status === "failed" && <div><div className="overline">Payment Not Completed</div><Link to="/shop" className="tt-btn-primary mt-8">Return to Shop</Link></div>}
    </div>
  );
}
